/**
 * AvatarGroup — renders a capped row of user avatars with an overflow chip.
 *
 * Shows up to `max` avatars (default 3). When the list exceeds `max`, a "+N"
 * chip is appended. Sizes are specified in pixels via the `size` prop (default
 * 24). Each individual avatar is rendered via the existing `<Avatar>` primitive.
 */

import { Avatar } from "./Avatar";

/** A minimal user record for AvatarGroup. */
export type AvatarGroupUser = {
  id: string;
  name: string;
  /** Uploaded photo URL; matches the `image` field on member/user records. */
  image?: string | null;
};

/** Props for `<AvatarGroup>`. */
export type AvatarGroupProps = {
  /** List of users to display. */
  users: ReadonlyArray<AvatarGroupUser>;
  /** Maximum number of avatars to show before collapsing to a "+N" chip. Default: 3. */
  max?: number;
  /** Avatar diameter in pixels. Default: 24. */
  size?: number;
};

/**
 * AvatarGroup component — a row of user avatars with an overflow indicator.
 *
 * @example
 * <AvatarGroup users={project.members} max={4} size={28} />
 */
export function AvatarGroup({ users, max = 3, size = 24 }: AvatarGroupProps) {
  const shown = users.slice(0, max);
  const overflow = users.length - shown.length;

  return (
    <div
      className="avatar-group"
      aria-label={`${users.length} member${users.length === 1 ? "" : "s"}`}
    >
      {shown.map((u) => (
        <Avatar
          key={u.id}
          id={u.id}
          name={u.name}
          size={size}
          image={u.image}
        />
      ))}
      {overflow > 0 && (
        <span
          className="avatar avatar-overflow"
          style={{
            width: size,
            height: size,
            fontSize: Math.round(size * 0.42),
          }}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
